/**
 * Data transformation utilities for Hotel Collection Detail page
 * Converts backend API response structures to UI component formats
 */

import {
  IExploreHotelGalleryItem,
  IExploreHotelDocumentItem,
  IExploreHotelVimeoVideo,
  IExploreHotelDetailData,
} from 'services/BackendApi/types/HotelCollectionDetailResponse';
import { IMediaImageUpload } from 'ui/MediaGallery/types';
import { IDocumentFile } from 'ui/DocumentLinks/types';

/**
 * Determine MIME type from file extension
 * Used when backend doesn't provide MIME type information
 */
export const getMimeTypeFromUrl = (url: string): string => {
  const extension = url
    .split('.')
    .pop()
    ?.toLowerCase();

  const mimeMap: Record<string, string> = {
    // Images
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    // Documents
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  };

  return mimeMap[extension || ''] || 'application/octet-stream';
};

/**
 * Transform backend gallery items to MediaGallery format
 * Note: Backend only provides single URL, so we use it for all size variants
 * This is a data limitation we must work with
 */
export const transformGalleryItemToMediaUpload = (item: IExploreHotelGalleryItem): IMediaImageUpload => {
  // Backend doesn't provide dimensions, use reasonable defaults
  const defaultWidth = 1200;

  return {
    displayName: item.displayName,
    fileType: 'image',
    isVimeoThumbnail: false,
    uuid: item.uuid, // Preserve UUID for bulk download feature
    sizes: {
      original: { path: item.url, mime: 'image/jpeg', width: defaultWidth },
      xxl: { path: item.url, mime: 'image/jpeg', width: defaultWidth },
      xl: { path: item.url, mime: 'image/jpeg', width: 1024 },
      l: { path: item.url, mime: 'image/jpeg', width: 800 },
      m: { path: item.url, mime: 'image/jpeg', width: 640 },
      s: { path: item.url, mime: 'image/jpeg', width: 400 },
      xs: { path: item.url, mime: 'image/jpeg', width: 200 },
    },
  };
};

/**
 * Transform array of gallery items to MediaGallery format
 */
export const transformGalleryItems = (items: IExploreHotelGalleryItem[]): IMediaImageUpload[] => {
  return items.map(transformGalleryItemToMediaUpload);
};

/**
 * Transform backend document items to DocumentLinks format
 * Backend doesn't provide MIME type, so we infer it from the URL extension
 */
export const transformDocumentItems = (items: IExploreHotelDocumentItem[]): IDocumentFile[] => {
  return items.map(item => ({
    displayName: item.displayName,
    fileType: 'file' as const,
    path: item.url,
    mime: getMimeTypeFromUrl(item.url),
  }));
};

/**
 * Create MediaImageUpload for Vimeo thumbnail if available
 * Uses largest available thumbnail size
 */
export const createVimeoThumbnailUpload = (vimeo: IExploreHotelVimeoVideo): IMediaImageUpload | null => {
  // Use largest available thumbnail
  const thumbnailUrl = vimeo.thumbnailLarge || vimeo.thumbnailMedium || vimeo.thumbnailSmall;

  if (!thumbnailUrl) return null;

  return {
    displayName: 'Video Preview',
    fileType: 'image',
    isVimeoThumbnail: true,
    sizes: {
      original: { path: thumbnailUrl, mime: 'image/jpeg', width: 1280 },
      xxl: { path: thumbnailUrl, mime: 'image/jpeg', width: 1280 },
      xl: { path: thumbnailUrl, mime: 'image/jpeg', width: 1024 },
      l: { path: thumbnailUrl, mime: 'image/jpeg', width: 800 },
      m: { path: thumbnailUrl, mime: 'image/jpeg', width: 640 },
      s: { path: thumbnailUrl, mime: 'image/jpeg', width: 400 },
      xs: { path: thumbnailUrl, mime: 'image/jpeg', width: 200 },
    },
  };
};

/**
 * Section configuration for navigation and rendering
 */
export interface IVisibleSection {
  id: string;
  label: string;
  hasContent: boolean;
}

/**
 * Determine which sections should be visible based on hotel data
 * Sections without content are excluded from both page and navigation
 */
export const getVisibleSections = (hotel: IExploreHotelDetailData): IVisibleSection[] => {
  const sections: IVisibleSection[] = [
    {
      id: 'details',
      label: 'Hotel Details',
      hasContent: true, // Always show if we have hotel data
    },
    {
      id: 'announcements',
      label: 'Announcements',
      hasContent: hotel.announcements.length > 0,
    },
    {
      id: 'rooms',
      label: 'Rooms',
      hasContent: hotel.accommodations.length > 0,
    },
    {
      id: 'gallery',
      label: 'Hotel Gallery',
      hasContent: hotel.gallery.length > 0,
    },
    {
      id: 'reviews',
      label: 'Reviews',
      hasContent: true, // Always show placeholder (per spec)
    },
    {
      id: 'documents',
      label: 'Hotel Documents',
      hasContent: hotel.documents.length > 0,
    },
    {
      id: 'policies',
      label: 'Policies',
      hasContent: !!(hotel.policiesAndRestrictions && hotel.policiesAndRestrictions.length > 0),
    },
    {
      id: 'contacts',
      label: 'Contacts',
      hasContent: !!(hotel.address || hotel.email || hotel.phoneNumber),
    },
  ];

  // Return only sections with content
  return sections.filter(section => section.hasContent);
};
